// DeepAI chat client. Hits our own backend proxy which fronts
// Claude Haiku 4.5 on AWS Bedrock (cross-region inference profile).
// The model is general-purpose — no AstroJyotish theming.
//
// Override the base URL at build time with REACT_APP_API_BASE.

import { getToken } from './auth';
import { getAnonUserId } from './localChats';

// Same-origin: hits the /api/* behavior on the sutrumai.com CloudFront
// distribution which routes to the dedicated DeepAI Lambda.
const API_BASE = (process.env.REACT_APP_API_BASE || '').replace(/\/$/, '');

/**
 * Send one chat turn to the Bedrock-backed assistant.
 *
 * @param {object} opts
 * @param {string} opts.question
 * @param {Array<{role:'user'|'assistant', content:string}>} [opts.conversationHistory]
 * @param {string} [opts.systemPrompt] - Persona prompt from the active
 *   assistant (Cricket Ninja, Chanakya, etc.). When omitted, the backend
 *   uses its default generic system prompt.
 * @param {AbortSignal} [opts.signal]
 * @returns {Promise<{ answer: string, model?: string, totalMs?: number }>}
 */
export async function deepaiChat({
  question,
  conversationHistory = [],
  systemPrompt = null,
  signal,
} = {}) {
  const body = { question, conversationHistory };
  if (systemPrompt) body.systemPrompt = systemPrompt;
  const token = getToken();
  // Anon UID is the fallback identifier — server keys quota on it
  // just like an email, so anon users now get the same 100/day cap
  // as signed-in ones, scoped to this browser.
  const uid = token ? null : getAnonUserId();
  const res = await fetch(`${API_BASE}/api/deepai/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token
        ? { Authorization: `Bearer ${token}` }
        : (uid ? { 'X-Anon-Uid': uid } : {})),
    },
    body: JSON.stringify(body),
    signal,
  });

  let data;
  try {
    data = await res.json();
  } catch {
    throw new Error(`Server returned a non-JSON response (HTTP ${res.status}).`);
  }

  if (!res.ok) {
    // Decorate the error so callers can detect quota exhaustion and
    // pop the right modal instead of the generic "try again" toast.
    const err = new Error(data?.error?.message || `Request failed (HTTP ${res.status}).`);
    err.status = res.status;
    err.code = data?.error?.code;
    if (data?.usage) err.usage = data.usage;
    if (data?.error?.kind) err.kind = data.error.kind;
    throw err;
  }

  return {
    answer: data.answer || '',
    model: data.model,
    totalMs: data.totalMs,
    // Post-call usage snapshot: { plan, limits, tokensToday,
    // tokensMonth, imagesToday, imagesMonth, dailyResetMs,
    // monthlyResetMs, subscription }. null for unidentified callers.
    usage: data.usage || null,
  };
}

// Read-only quota probe — used by the header badge to show "X/100"
// without burning a chat call. Works for both signed-in users and
// anonymous browsers (server keys on the same identifier the chat
// endpoint uses).
export async function getDailyQuota({ signal } = {}) {
  const token = getToken();
  const uid = token ? null : getAnonUserId();
  const res = await fetch(`${API_BASE}/api/deepai/quota`, {
    method: 'GET',
    headers: {
      ...(token
        ? { Authorization: `Bearer ${token}` }
        : (uid ? { 'X-Anon-Uid': uid } : {})),
    },
    signal,
  });
  if (!res.ok) return null;
  try {
    const data = await res.json();
    // Server now returns { usage, planLimits }. Surface the usage
    // snapshot directly — limits are nested inside it.
    return data?.usage || null;
  } catch {
    return null;
  }
}
